import React, { useEffect, useState } from "react";
import { Button, Alert } from "react-bootstrap";
import { X, Pencil, Trash, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../../_css/AddRoleplay.module.css";
import CustomRoleDeleteModal from "../../Modals/CustomRoleDeleteModal";
import RolePlayPublishedModal from "../../Modals/RolePlayPublishedModal";
import DataLoader from "../../common/DataLoader";
import { getPredefinedRoleplaysDeatils } from "../../../requests";
import { startLoader, stopLoader } from "../../../_store/reducers/loaderReducer";

const PreviewAndStart = ({
    publishingRoleplay,
    deleteRoleplay,
    handleBack,
    roleplay_id,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoading = useSelector((state) => state.loader.isLoading);

    const [showShortPreview, setShortPreview] = useState(true);
    const [isExpanded, setIsExpanded] = useState(true);
    const [showPublishedModal, setShowPublishedModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    // Roleplay Details State
    const [roleplayDetails, setRoleplayDetails] = useState(null);
    const [error, setError] = useState(null);

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editableTitle, setEditableTitle] = useState("");

    // --- FIX: Initialize State Directly from LocalStorage ---
    // This runs only once on mount. If 'selected_language' exists, it uses it immediately.
    // Otherwise, it defaults to "English".
    const [selectedLanguage] = useState(() => {
        return localStorage.getItem("selected_language") || "English";
    });

    // --- Helper to safely parse JSON strings ---
    const safeParse = (data) => {
        if (Array.isArray(data)) return data;
        try {
            return JSON.parse(data);
        } catch (e) {
            return [];
        }
    };

    // --- Fetch Data ---
    useEffect(() => {
        const fetchRoleplay = async () => {
            // Guard clause: ensure we have an ID and a language before fetching
            if (!roleplay_id || !selectedLanguage) return;

            // dispatch(startLoader());
            setError(null);

            try {
                // Determine if we need to pass the language parameter
                // If the backend expects it in the body/query, ensure it's passed here
                const response = await getPredefinedRoleplaysDeatils({
                    roleplay_id,
                    selectedLanguage
                });

                let normalizedData = null;

                // Case 1: English Structure (Object wrapped in roleplay)
                if (response?.roleplay) {
                    normalizedData = {
                        avatar_role: response.roleplay.avatar_role,
                        scenario_description: response.roleplay.scenario_description,
                        selected_competencies: response.roleplay.selected_competencies || [],
                        learning_goals: response.roleplay.learning_goals || [],
                        difficulty_level: response.roleplay.difficulty_level,
                        avatar_emotion: response.roleplay.avatar_emotion
                    };
                }
                // Case 2: Dutch/Other Structure (Array of objects)
                else if (Array.isArray(response) && response.length > 0) {
                    const raw = response[0];
                    normalizedData = {
                        avatar_role: raw.name || raw.title,
                        scenario_description: raw.scenario || raw.short_description,
                        selected_competencies: safeParse(raw.competencies),
                        learning_goals: safeParse(raw.learning_goals),
                        difficulty_level: raw.difficulty_level || "Medium",
                        avatar_emotion: raw.avatar_emotion || "Neutral"
                    };
                }

                if (normalizedData) {
                    setRoleplayDetails(normalizedData);
                    setEditableTitle(normalizedData.avatar_role || "");
                } else {
                    console.warn("Unknown response structure:", response);
                    setError("Received unexpected data format from server.");
                }

            } catch (err) {
                console.error("Failed to fetch roleplay details:", err);
                const errorMessage = err.response?.data?.message || "Failed to load roleplay details.";
                setError(errorMessage);
            } finally {
                // dispatch(stopLoader());
            }
        };

        fetchRoleplay();
    }, [roleplay_id, selectedLanguage, dispatch]);

    const handleTitleSave = () => {
        setRoleplayDetails((prev) => ({ ...prev, avatar_role: editableTitle }));
        setIsEditingTitle(false);
    };

    const handleModalPreview = async () => {
        setIsPublishing(true);
        try {
            const res = await publishingRoleplay({ updated_title: editableTitle?.trim() || null });
            if (res) {
                sessionStorage.setItem("custom_roleplay", JSON.stringify(res.roleplay_info));
                setShowPublishedModal(true);
            }
        } catch (err) {
            console.error("Failed to publish:", err);
        } finally {
            setIsPublishing(false);
        }
    };

    const handleDeleteRoleplay = async () => {
        dispatch(startLoader());
        try {
            await deleteRoleplay();
            navigate("/roleplayManagement");
        } catch (err) {
            console.error("Failed to delete:", err);
        } finally {
            dispatch(stopLoader());
            setShowDeleteModal(false);
        }
    };

    const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

    const handlePublishedModalClose = () => {
        setShowPublishedModal(false);
        navigate("/roleplayManagement");
    };

    // 1. Show Error State if fetch failed
    if (error) {
        return (
            <div className={`${styles.stepCard} ${styles.previewContainer} d-flex flex-column align-items-center justify-content-center`}>
                <Alert variant="danger" className="text-center w-75">
                    <AlertCircle size={24} className="mb-2" />
                    <h5>Error Loading Roleplay</h5>
                    <p>{error}</p>
                    <Button variant="outline-danger" onClick={handleBack} size="sm">
                        Go Back
                    </Button>
                </Alert>
            </div>
        );
    }

    return (
        <div className={`${styles.stepCard} ${styles.previewContainer}`}>

            {/* Loading Overlay */}
            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <DataLoader />
                </div>
            )}

            {/* Header */}
            <div>
                <span className={styles.pageTitle}>
                    Preview & Publish
                </span>
            </div>

            {/* Info Box */}
            {showShortPreview && (
                <div className={styles.infoBox}>
                    <div className={styles.infoContent}>
                        <span className={styles.infoTextBold}>
                            <span className={styles.infoIcon}>i</span> Review your role play below.
                        </span>
                        <span className={styles.infoText}>
                            You can still edit everything after publishing. All your inputs are shown exactly as they will appear. After publishing, your role play will be available in the section
                        </span>
                        <span className={styles.infoLink}>
                            PractAIce .
                        </span>
                    </div>
                    <X size={16} style={{ cursor: "pointer", opacity: 0.6 }} onClick={() => setShortPreview(false)} />
                </div>
            )}

            {/* Main Content Card - Only render if we have details */}
            {roleplayDetails && (
                <div className={styles.mainCard} style={{ opacity: isLoading ? 0.5 : 1 }}>

                    {/* Title & Description Section */}
                    <div style={{ display: "flex", flexDirection: "column" }}>

                        {/* Title Edit Box */}
                        <div className={styles.cardHeader}>
                            {isEditingTitle ? (
                                <input
                                    autoFocus
                                    className={styles.roleTitleInput}
                                    value={editableTitle}
                                    onChange={(e) => setEditableTitle(e.target.value)}
                                    onBlur={handleTitleSave}
                                    onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
                                />
                            ) : (
                                <>
                                    <h5 className={styles.roleTitle}>{roleplayDetails?.avatar_role}</h5>
                                    <Pencil size={24} style={{ cursor: "pointer", opacity: 0.6 }} onClick={() => setIsEditingTitle(true)} />
                                </>
                            )}
                        </div>

                        {/* Scenario Description */}
                        <div className={styles.scenarioText}>
                            <span className={styles.scenarioLabel}>📝 Scenario: {roleplayDetails?.avatar_role}</span>
                            <div className={!isExpanded ? styles.clampedText : ""}>
                                {roleplayDetails?.scenario_description}
                            </div>
                            <span onClick={() => setIsExpanded(!isExpanded)} className={styles.readMore}>
                                {isExpanded ? "Read Less" : "Read More..."}
                            </span>
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div className="row">
                        <div className="col-md-6">
                            <div className={styles.sectionHeader}>
                                🧠 <span className="ms-2">Trained Competences:</span>
                            </div>
                            <ul className={styles.detailList}>
                                {roleplayDetails?.selected_competencies?.map((item, i) => (
                                    <li key={i}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="col-md-6">
                            <div className="mb-3">
                                <div className={styles.sectionHeader}>
                                    ⚡ <span className="ms-2">Difficulty Level:</span>
                                </div>
                                <ul className={styles.detailList}>
                                    <li>{capitalize(roleplayDetails?.difficulty_level)}</li>
                                </ul>
                            </div>
                            <div>
                                <div className={styles.sectionHeader}>
                                    🎭 <span className="ms-2">Avatar Emotion:</span>
                                </div>
                                <ul className={styles.detailList}>
                                    <li>{capitalize(roleplayDetails?.avatar_emotion)}</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Learning Goals */}
                    <div>
                        <div className={styles.sectionHeader}>🎯 Key Learning Goals:</div>
                        <ul className={styles.detailList}>
                            {roleplayDetails?.learning_goals?.map((goal, i) => (
                                <li key={i}>{goal}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Footer Actions */}
            <div className={styles.footerActions}>
                <Button
                    className={styles.backButton}
                    onClick={handleBack}
                    disabled={isLoading}
                >
                    Back
                </Button>
                <Button
                    onClick={handleModalPreview}
                    disabled={isLoading || !roleplayDetails || isPublishing}
                    className={`${styles.actionButton} ${styles.publishButton}`}
                >
                    {isPublishing ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Publishing...
                        </>
                    ) : "Publish"}
                </Button>
                <button
                    onClick={() => setShowDeleteModal(true)}
                    disabled={isLoading || !roleplayDetails}
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                >
                    <Trash size={18} /> Delete
                </button>
            </div>

            {/* Modals */}
            <RolePlayPublishedModal
                show={showPublishedModal}
                onHide={handlePublishedModalClose}
                delay={10000}
            />

            <CustomRoleDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onDelete={handleDeleteRoleplay}
            />
        </div>
    );
};

export default PreviewAndStart;